import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  DialogContent,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import {
  Badge as BadgeIcon,
  BadgeDollarSign,
  Link,
  ExternalLink,
  RefreshCcw,
} from "lucide-react";
import { AddDeleteCartButtons } from "modules/cart/components/cartElements";
import { AddCartMethod } from "modules/cart/types/add-cart-methods";
import { CartItem, Payload, PriceComponent } from "modules/cart/types/cart";
import { StoreData } from "modules/main/types/storeData";
import { ProductSheet } from "modules/product/components/product-sheet";
import React, { useState } from "react";
import { forwardRef } from "react";
import Amount from "utils/amount";
import { get_total_qty_per_prod } from "utils/cartUtils";
import { isDesktopSize } from "utils/is-desktop";
import { getStoreBdData } from "utils/storeDataManager";
import { CartCreationStatus } from "../types/optimizedCart";

interface ProductTableProps {
  className?: string;
  filterCart: CartItem[];
  cart: CartItem[];
  disableButtons?: boolean;
  loading?: boolean;
  storeList: StoreData[];
  mode: "RESULT" | "VIEW";
  noClick?: boolean;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
  addDeletefromCart: (payload: Payload) => void;
  removeFromCart?: (payload: Payload) => void;
  refreshTrigger?: () => void;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  notFoundsFlag?: boolean;
}

export const ProductTable = forwardRef<HTMLTableElement, ProductTableProps>(
  ({ ...props }, ref) => {
    const isDesktop = useMediaQuery(isDesktopSize);
    return isDesktop ? (
      <ProductTableDesktop {...props} />
    ) : (
      <ProductTableMobile {...props} />
    );
  },
);

const ProductTableDesktop = forwardRef<HTMLTableElement, ProductTableProps>(
  ({ className, filterCart, ...props }, ref) => {
    const [noReplaceStates, setNoReplaceStates] = useState<{
      [key: string]: boolean;
    }>({});

    const newProductTab = (url: string) => {
      const windowName = "carriapp_product_window";
      window.open(url, windowName);
    };

    return (
      <Table className={cn("", className)} ref={ref}>
        <TableHeader>
          <TableRow>
            <TableHead className="hidden w-[100px] sm:table-cell">
              <span className="sr-only">Image</span>
            </TableHead>
            <TableHead>Producto</TableHead>
            <TableHead className="text-right">Precio Unitario</TableHead>
            {props.mode === "RESULT" && (
              <TableHead className="text-center">Cantidad</TableHead>
            )}
            {/* <TableHead className="text-center">Tienda</TableHead> */}
            {/* <TableHead className="text-right">
                        Ahorras
                    </TableHead>                     */}
            <TableHead className="text-right hidden md:table-cell">
              {props.notFoundsFlag ? "Referencia" : "Sub Total"}
            </TableHead>
            {/* <TableHead>
                    <span className="sr-only">Url</span>
                    </TableHead> */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.loading
            ? [...Array(4)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell className="w-96">
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="w-full h-20" />
                  </TableCell>
                  {/* <TableCell><Skeleton className="w-full h-20"/></TableCell> */}
                  {/* <TableCell><Skeleton className="w-full h-20"/></TableCell> */}
                </TableRow>
              ))
            : filterCart.map((item, index) => {
                const basePc = item.price_component[0];
                const factor = basePc.factor ?? 1;
                const isPromo = item.price_component
                  .filter((pc) => !pc.no_met_conditions)
                  .some((pc) => pc.is_promo);
                const quantity = basePc.quantity;
                const price = basePc.price / (factor * quantity);
                const worst_price = item.price_component
                  .filter((pc) => !pc.no_met_conditions)
                  .reduce((acc, pc) => acc + pc.worst_price, 0);

                const cartElement = props.cart.find((ci) => ci.id === item.id);
                const imageUrl = basePc?.images?.catalog ?? basePc?.image_url;
                const replacedImageUrl =
                  basePc?.replaced_images?.thumbs ?? basePc?.replaced_image_url;

                return (
                  <TableRow key={`${index}`}>
                    <TableCell className="hidden sm:table-cell">
                      <div
                        className="min-w-20 cursor-pointer"
                        onClick={() => newProductTab(basePc.url)}
                      >
                        <img className="Image-80" src={imageUrl} alt={""} />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium flex flex-col gap-2 items-start justify-center">
                      <Dialog>
                        <DialogTitle></DialogTitle>
                        <DialogDescription></DialogDescription>
                        <DialogTrigger>
                          <span className="font-semibold">{item.brand}</span> -{" "}
                          <span className="font-normal">{basePc.name}</span>
                        </DialogTrigger>
                        <DialogContent className="min-w-[70%]">
                          <ProductSheet productID={item.slug} />
                        </DialogContent>
                      </Dialog>
                      {(basePc.replaced_name ||
                        basePc.replaced_image_url ||
                        isPromo ||
                        !item.met_conditions) && (
                        <div className="flex flex-row gap-2 items-center">
                          {(basePc.replaced_name ||
                            basePc.replaced_image_url ||
                            replacedImageUrl) && (
                            <div className="flex flex-row gap-2 items-center">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant={"secondary"}
                                    size={"xs"}
                                    className="text-foreground flex items-center"
                                  >
                                    <RefreshCcw className="w-4 h-4" />
                                    <span className="text-xxs">Reemplazo</span>
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="flex flex-col gap-4">
                                  <div className="flex flex-row gap-4 items-center">
                                    <div className="min-w-16">
                                      <img
                                        className="Image-60"
                                        src={replacedImageUrl}
                                        alt={""}
                                      />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <span className="font-semibold text-xs">
                                        {basePc.replaced_name}
                                      </span>
                                      {basePc.replaced_price && (
                                        <div className="flex gap-1">
                                          <Amount
                                            className="font-semibold text-xs text-muted-foreground"
                                            amount={basePc.replaced_price}
                                            currency="CLP"
                                            locale="es-CL"
                                          />{" "}
                                          <span className="text-muted-foreground text-xxs font-semibold">
                                            c/u
                                          </span>
                                        </div>
                                      )}
                                      <div className="text-muted-foreground text-xs">
                                        <span className="font-semibold text-foreground">
                                          {quantity}
                                        </span>{" "}
                                        Unidades
                                        {basePc.replaced_price && (
                                          <Amount
                                            className="font-semibold text-foreground text-xs ml-1"
                                            amount={
                                              basePc.replaced_price * quantity
                                            }
                                            currency="CLP"
                                            locale="es-CL"
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* <div className="flex items-center gap-2 px-2">
                                                <Switch 
                                                    id={`no-replace-${item.id}`}
                                                    checked={noReplaceStates[item.id] || false}
                                                    onCheckedChange={(checked) =>{                                                             
                                                        setNoReplaceStates(prev => ({
                                                            ...prev,
                                                            [item.id]: checked
                                                        }));
                                                        props.setNoReplace(item.id, "FIXED" as AddCartMethod)
                                                    }}                                                
                                                />
                                                <Label htmlFor="no-replace" className="text-xs">Quiero el original</Label>
                                            </div> */}
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                          {isPromo && (
                            <div className="best-price w-full flex flex-row gap-2 items-center text-xxs">
                              <BadgeIcon className="w-4 h-4" />
                              ¡Oferta!
                            </div>
                          )}
                          {!item.met_conditions && (
                            <Badge variant="destructive">
                              No tienes el min de compra
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {basePc.worst_price > basePc.price && (
                        <span className="text-muted-foreground line-through">
                          <Amount
                            amount={worst_price / quantity}
                            currency="CLP"
                            locale="es-CL"
                          />
                        </span>
                      )}
                      <span>
                        <Amount
                          className="ml-2"
                          amount={price}
                          currency="CLP"
                          locale="es-CL"
                        />
                      </span>
                    </TableCell>
                    {props.mode === "RESULT" && (
                      <TableCell className="text-center">
                        {/*
                            (!props.disableButtons) ?
                            <AddDeleteCartButtons 
                                className=""
                                product_id={item.id}
                                sku={basePc.sku}
                                name={item.price_component[0]?.name}
                                displayQuantity={quantity*factor}                               
                                quantity={quantity*factor}
                                method={item.method}
                                PlusMinusCallback={props.addDeletefromCart ?? (() => {})}   
                                showDelete={false}                        
                            />:<span className="font-semibold">{quantity*factor}</span>
                        */}
                        {cartElement && (
                          <AdditionalStoresInfo
                            cartElement={cartElement}
                            currentStoreId={item.price_component[0]?.id}
                            storeList={props.storeList}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="hidden md:table-cell text-right font-semibold">
                      <Amount
                        amount={basePc.price}
                        currency="CLP"
                        locale="es-CL"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
    );
  },
);

export const ProductTableMobile: React.FC<ProductTableProps> = ({
  filterCart,
  ...props
}) => {
  const [noReplaceStates, setNoReplaceStates] = useState<{
    [key: string]: boolean;
  }>({});

  return (
    <div className={cn("flex flex-col gap-2", props.className)}>
      {filterCart.map((ci, i) => {
        return (
          <React.Fragment key={`${i}`}>
            <ProductTableItem
              key={`${i}`}
              product={ci}
              disableButtons={props.disableButtons}
              total={get_total_qty_per_prod(ci.id, filterCart)}
              priceComponent={ci.price_component}
              brand={ci.brand}
              storeList={props.storeList}
              mode={props.mode}
              filterCart={filterCart}
              addDeletefromCart={props.addDeletefromCart}
              noReplaceStates={noReplaceStates}
              setNoReplaceStates={setNoReplaceStates}
              setNoReplace={props.setNoReplace}
              cartCreationStatus={props.cartCreationStatus}
              setCartStatus={props.setCartStatus}
              noClick={props.noClick}
            />
            {!(i === filterCart.length - 1) && (
              <Separator className="w-full p-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface ProductTableItemProps extends React.HTMLAttributes<HTMLDivElement> {
  product: CartItem;
  priceComponent: PriceComponent[];
  brand: string;
  total: number;
  exceded?: boolean;
  disableButtons?: boolean;
  storeList: StoreData[];
  mode?: "RESULT" | "VIEW";
  filterCart: CartItem[];
  addDeletefromCart?: (payload: Payload) => void;
  noReplaceStates: { [key: string]: boolean };
  setNoReplaceStates: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  setNoReplace: (productId: string, newMethod: AddCartMethod) => void;
  cartCreationStatus: CartCreationStatus[];
  setCartStatus: React.Dispatch<React.SetStateAction<CartCreationStatus[]>>;
  noClick?: boolean;
  // removeFromCart?: (payload:Payload) => void,
  // refreshTrigger?:() => void,
}

const ProductTableItem = forwardRef<HTMLDivElement, ProductTableItemProps>(
  ({ product, priceComponent: p, ...props }, ref) => {
    const basePc = product.price_component[0];
    const quantity = basePc?.quantity ?? 1;
    const factor = basePc?.factor ?? 1;
    const price = basePc.price / (quantity * factor);
    const worst_price = basePc.worst_price / quantity;
    const discountPercent =
      worst_price > 0 ? Math.max(0, (worst_price - price) / worst_price) : 0;
    const isPromo = p.some((pc) => pc.is_promo);
    const no_met_conditions = basePc?.no_met_conditions;

    const imageUrl = basePc?.images?.thumbs ?? basePc?.image_url;
    const replacedImageUrl =
      basePc?.replaced_images?.thumbs ?? basePc?.replaced_image_url;
    return (
      <div className="flex w-full flex-row gap-4 items-center">
        <div>
          <div className="min-w-20 cursor-pointer relative">
            <img className="Image-80" src={imageUrl} alt={""} />
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full items-start">
          <div className="flex flex-row gap-4 items-center w-full">
            <div className="flex flex-col text-xs w-full gap-2">
              <span className="font-semibold">{p[0].name}</span>
              <div className="flex flex-row gap-2 justify-between w-full">
                <div>
                  <span className="text-xs font-semibold text-muted-foreground">
                    {props.brand}
                  </span>
                  <div className="flex gap-1">
                    <Amount
                      className="font-semibold text-muted-foreground"
                      amount={price}
                      currency="CLP"
                      locale="es-CL"
                    />{" "}
                    <span className="text-muted-foreground text-xxs font-semibold">
                      c/u
                    </span>
                    {worst_price > price && (
                      <span className="text-muted-foreground line-through ml-1">
                        <Amount
                          amount={worst_price}
                          currency="CLP"
                          locale="es-CL"
                        />
                      </span>
                    )}
                    {/* {(isPromo)&&(
                                    <Badge className="mx-2 text-xxs bg-green-600">
                                        <Amount 
                                            className="font-semibold text-white"
                                            amount={discountPercent} 
                                            formatStyle="percent"
                                            locale="es-CL" 
                                            />    
                                    </Badge>)}                                                              */}
                  </div>
                </div>
                {/*
			(props.mode === "RESULT" && !props.disableButtons)?(    
                            <>
                            <AddDeleteCartButtons 
                                className=""
                                product_id={product.id}
                                sku={product.price_component[0]?.sku}
                                name={product.price_component[0]?.name}
                                displayQuantity={quantity * factor}                               
                                quantity={quantity*factor}
                                method={product.method}
                                PlusMinusCallback={props.addDeletefromCart ?? (() => {})}   
                                showDelete={false}                        
                            />
                            </>
                        ):(<span className="text-sm font-semibold">{quantity*factor}</span>)
			*/}
              </div>
            </div>
          </div>
          <Separator className="w-full p-0" />
          <div className="flex w-full flex-row justify-between items-center text-white">
            <div>
              {(basePc.replaced_name ||
                basePc.replaced_image_url ||
                replacedImageUrl) && (
                <div className="flex flex-row gap-2 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"secondary"}
                        size={"xs"}
                        className="text-foreground flex items-center"
                      >
                        <RefreshCcw className="w-4 h-4" />
                        <span className="text-xxs">Reemplazo</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="flex flex-col gap-4">
                      <div className="flex flex-row gap-4 items-center">
                        <div className="min-w-16">
                          <img
                            className="Image-60"
                            src={replacedImageUrl}
                            alt={""}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-xs">
                            {basePc.replaced_name}
                          </span>
                          {basePc.replaced_price && (
                            <div className="flex gap-1">
                              <Amount
                                className="font-semibold text-xs text-muted-foreground"
                                amount={basePc.replaced_price}
                                currency="CLP"
                                locale="es-CL"
                              />{" "}
                              <span className="text-muted-foreground text-xxs font-semibold">
                                c/u
                              </span>
                            </div>
                          )}
                          <div className="text-muted-foreground text-xs">
                            <span className="font-semibold text-foreground">
                              {quantity}
                            </span>{" "}
                            Unidades
                            {basePc.replaced_price && (
                              <Amount
                                className="font-semibold text-foreground text-xs ml-1"
                                amount={basePc.replaced_price * quantity}
                                currency="CLP"
                                locale="es-CL"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {/* <div className="flex items-center gap-2 px-2">
                                    <Switch 
                                            id={`no-replace-${product.id}`}
                                            checked={props.noReplaceStates[product.id] || false}
                                            onCheckedChange={(checked) =>{ 
                                                
                                                props.setNoReplaceStates(prev => ({
                                                    ...prev,
                                                    [product.id]: checked
                                                }));
                                                props.setNoReplace(product.id, "FIXED" as AddCartMethod)
                                            }}                                                
                                    />
                                    <Label htmlFor="no-replace" className="text-xs">Quiero el original</Label>
                                </div> */}
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>
            {/* <div className="flex flex-col gap-2 text-xs">
                    {
                    p.map((pc,i) => {
                        return(
                        <div className="flex flex-row gap-2 items-center" key={i}>
                            <a href={pc.url} target="_blank" rel="noopener noreferrer">
                            </a>
                            <div className="flex flex-col w-full gap-2 justify-start text-white">
                                <div className={cn("flex w-full gap-2",
                                    {
                                        "opacity-40":no_met_conditions,
                                        "line-through": no_met_conditions
                                    })}>
                                    
                                </div>
                            </div>
                        </div>
                        )
                    })
                    }


                </div> */}
            <div className="text-xs">
              <span className="text-muted-foreground">
                Referencia:
                <Amount
                  className="ml-2 font-semibold text-foreground"
                  amount={basePc.price}
                  currency="CLP"
                  locale="es-CL"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

const getOtherStoreComponents = (
  cartElement: CartItem,
  currentStoreId: number,
) => {
  if (!cartElement?.price_component) return [];
  return cartElement.price_component.filter((pc) => pc.id !== currentStoreId);
};

// Componente separado para las tiendas adicionales
const AdditionalStoresInfo = ({
  cartElement,
  currentStoreId,
  storeList,
}: {
  cartElement: CartItem;
  currentStoreId: number;
  storeList: StoreData[];
}) => {
  const otherStores = getOtherStoreComponents(cartElement, currentStoreId);

  if (otherStores.length === 0) return null;

  const totalQuantity = otherStores.reduce((acc, pc) => acc + pc.quantity, 0);

  return (
    <Badge
      variant="outline"
      className="flex flex-row w-fit mx-auto px-4 items-center justify-center gap-2 text-xs my-2 font-semibold"
    >
      <span>{totalQuantity} más en</span>
      {otherStores.map((pc, index) => (
        <Avatar key={index} className="w-4 h-4">
          <AvatarImage src={getStoreBdData(pc.id, storeList)?.logo_url} />
        </Avatar>
      ))}
    </Badge>
  );
};
